/**
 * Pulsação de Alarme.
 *
 * Marca **mudança**, não estado: pulsa o que acabou de mudar de estado entre um
 * ciclo e o outro. Pulsar por estado deixaria 40% da grade em movimento
 * permanente — e movimento que nunca para deixa de informar.
 */

/**
 * Quantidade de defasagens distintas.
 *
 * Oito bastam para quebrar o uníssono sem multiplicar regras de CSS.
 */
export const PULSE_PHASE_COUNT = 8;

/**
 * Fotografa o estado corrente de cada Ativo, para comparar no próximo ciclo.
 *
 * @param {Object[]} assets Lista plana de `buildAssets`.
 * @returns {Object} Mapa de chave do Ativo para o seu estado.
 */
export function snapshotStates(assets) {
  return (assets || []).reduce((snapshot, asset) => {
    // eslint-disable-next-line no-param-reassign
    snapshot[asset.key] = asset.state;
    return snapshot;
  }, {});
}

/**
 * Chaves dos Ativos cujo estado mudou entre dois ciclos.
 *
 * Ativo sem estado anterior não conta como mudança: no primeiro carregamento, e
 * ao trocar o escopo, a frota inteira seria "nova" e pulsaria de uma vez.
 *
 * @param {Object} previous Retrato do ciclo anterior, ou null no primeiro.
 * @param {Object} current Retrato do ciclo atual.
 * @returns {String[]} Chaves que mudaram de estado.
 */
export function diffStates(previous, current) {
  if (!previous) {
    return [];
  }

  return Object.keys(current || {})
    .filter(key => previous[key] !== undefined && previous[key] !== current[key]);
}

/**
 * Defasagem do cartão, derivada da própria chave.
 *
 * Vem da chave e não da posição na grade de propósito: os cartões reordenam
 * quando há filtro ativo, e uma defasagem posicional faria o padrão inteiro se
 * reembaralhar exatamente quando o operador acabou de agir.
 *
 * @param {String} key Chave do cartão.
 * @returns {Number} Índice de fase entre 0 e PULSE_PHASE_COUNT - 1.
 */
export function getPulsePhase(key) {
  const text = String(key || '');
  let hash = 0;

  for (let index = 0; index < text.length; index += 1) {
    // Variante de djb2: determinística e barata, sem dependência externa.
    hash = ((hash * 33) + text.charCodeAt(index)) % 100003;
  }

  return hash % PULSE_PHASE_COUNT;
}

/**
 * Marca os cartões que contêm algum Ativo recém-mudado.
 *
 * O cartão inteiro pulsa, e não só a faixa: a 3 metros a faixa é pequena demais
 * para ser localizada numa grade de 81.
 *
 * @param {Object[]} groups Cartões devolvidos por `groupAssets`.
 * @param {String[]} changedKeys Chaves de `diffStates`.
 * @returns {Object[]} Cartões com a marca `changed`.
 */
export function markChangedGroups(groups, changedKeys) {
  const changed = changedKeys || [];

  return (groups || []).map(group => ({
    ...group,
    changed: group.assets.some(asset => changed.indexOf(asset.key) >= 0),
  }));
}
